import React, { useState } from 'react';
import { useAccount } from 'wagmi';
import { useAppKit } from '@reown/appkit/react';
import { useQuery } from '@tanstack/react-query';
import {
  Coins,
  Crown,
  ArrowRightLeft,
  Plus,
  BarChart3,
  TrendingUp,
  Loader2,
  HelpCircle,
  Wallet,
  AlertCircle,
} from 'lucide-react';
import { getUserInfo } from '../authAdapter';
import type { UserRankResponse } from '../authAdapter';
import { formatEther } from 'viem';

const SHOW_APR_BOOST = import.meta.env.VITE_SHOW_APR_BOOST === 'true';
const ACTION_BUTTON_CLASS =
  'w-full md:w-[130px] mt-2 px-4 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-primarybrand/60 via-secondarybrand/60 to-primarybrand/20 hover:from-primarybrand/40 hover:via-secondarybrand/40 hover:to-primarybrand/40 rounded-lg transition-all duration-300 border border-primarybrand/30 hover:border-primarybrand/50 cursor-pointer shadow-[0_0_15px_rgba(154,238,230,0.15)] hover:shadow-[0_0_25px_rgba(154,238,230,0.3)]';

interface DashboardProps {
  isAuthenticated?: boolean;
  onVerifyClick?: () => void;
}

export const Dashboard: React.FC<DashboardProps> = ({
  isAuthenticated,
  onVerifyClick,
}) => {
  const { isConnected, address } = useAccount();
  const { open } = useAppKit();

  const { data: dashboardData, isLoading: loading } = useQuery({
    queryKey: ['dashboard-data', address, isAuthenticated],
    queryFn: async () => {
      if (!address) return null;
      return await getUserInfo();
    },
    enabled: !!address && isConnected && !!isAuthenticated,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const totalAprmon = dashboardData?.totalLiquidAprMon || '0';
  const lockedAprmon = dashboardData?.totalLockedAprMon || '0';
  const userInfo = dashboardData;

  // Format value
  const formatValue = (value: string | number | undefined): string => {
    if (value === undefined || value === null) return '--';

    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return value.toString();

    if (Number.isInteger(numValue)) {
      return numValue.toLocaleString();
    }

    if (numValue >= 1000) {
      return numValue.toLocaleString(undefined, { maximumFractionDigits: 2 });
    } else if (numValue >= 1) {
      return numValue.toLocaleString(undefined, { maximumFractionDigits: 2 });
    } else {
      return numValue.toFixed(4);
    }
  };

  const formatRank = (rank: number | undefined | null): string => {
    if (rank === undefined || rank === null) return '--';

    const totalUsers = userInfo?.totalUsers;

    // If rank is within top 1000, show the specific number
    if (rank <= 1000) {
      if (totalUsers) {
        return `${rank.toLocaleString()}`;
      }
      return rank.toLocaleString();
    }

    // If totalUsers > 1000, show percentage tiers for ranks beyond 1000
    if (totalUsers && totalUsers > 1000) {
      const percentage = (rank / totalUsers) * 100;

      // Define percentage tiers
      const tiers = [0.1, 1, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50];

      for (const tier of tiers) {
        if (percentage <= tier) {
          return `Top ${tier}%`;
        }
      }

      // If beyond 50%, show the actual percentage rounded
      return `Top ${Math.ceil(percentage)}%`;
    }

    // Fallback
    return rank.toLocaleString();
  };

  const formatCurrency = (value: string | number | undefined): string => {
    if (value === undefined || value === null) return '$0.00';

    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return '$0.00';

    return `$${numValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatAddress = (address: string): string => {
    if (!address) return '';
    if (address.length <= 10) return address;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  // Calculate next update time (UTC 1:00)
  const getNextUpdateTime = (): string => {
    const now = new Date();
    const nextUpdate = new Date();
    nextUpdate.setUTCHours(1, 0, 0, 0);

    // If current time is past 1 AM UTC today, set to tomorrow
    if (now.getTime() >= nextUpdate.getTime()) {
      nextUpdate.setUTCDate(nextUpdate.getUTCDate() + 1);
    }

    // Format as UTC time with date: MM/DD HH:mm UTC
    const month = (nextUpdate.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = nextUpdate.getUTCDate().toString().padStart(2, '0');
    const hours = nextUpdate.getUTCHours().toString().padStart(2, '0');
    const minutes = nextUpdate.getUTCMinutes().toString().padStart(2, '0');
    return `${month}/${day} ${hours}:${minutes} UTC`;
  };

  return (
    <div className="w-full mb-8 font-['Poppins']">
      {isConnected && address && (
        <div className="text-[24px] font-semibold text-white mb-6">
          Hi, {formatAddress(address)}
        </div>
      )}
      <div className="relative w-full rounded-3xl overflow-hidden">
        <div className="absolute inset-0 bg-[url('/apr-points/bg_leaderboard.png')] bg-cover bg-center opacity-[0.08]" />
        <div className="absolute inset-0 bg-primarybrand opacity-10 pointer-events-none" />
        <div
          className="absolute inset-0 rounded-3xl pointer-events-none"
          style={{
            border: '1px solid transparent',
            background:
              'linear-gradient(94.02deg, rgba(58, 236, 167, 0.48) 0%, rgba(154, 238, 230, 0.24) 100%) border-box',
            WebkitMask:
              'linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
          }}
        />
        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 p-4 md:p-8">
          {/* Left Side */}
          <div className="flex flex-col">
            <div className="flex flex-wrap items-start gap-8 md:gap-16">
              {/* Total Points */}
              <div>
                <div className="flex items-center gap-2 text-white mb-3">
                  <Coins className="w-5 h-5" />
                  <span className="text-base font-medium">Total Points</span>

                  <HelpCircle
                    className="w-4 h-4 text-gray-500 cursor-pointer hover:text-gray-300"
                    onClick={() =>
                      window.open(
                        'https://apriori-docs.gitbook.io/apriori-docs/apr-token/apr-boost/what-is-apr-boost',
                        '_blank'
                      )
                    }
                  />
                </div>
                <div className="text-3xl md:text-[42px] leading-none font-semibold text-primarybrand">
                  {loading ? (
                    <Loader2 className="w-8 h-8 animate-spin text-primarybrand" />
                  ) : userInfo?.points ? (
                    formatValue(userInfo.points.value)
                  ) : (
                    '--'
                  )}
                </div>
              </div>

              {/* Rank */}
              <div>
                <div className="flex items-center gap-2 text-gray-500 mb-3">
                  <Crown className="w-5 h-5" />
                  <span className="text-base font-medium">Rank</span>
                </div>
                <div className="text-3xl md:text-[42px] leading-none font-semibold text-white">
                  {loading ? (
                    <Loader2 className="w-8 h-8 animate-spin text-white" />
                  ) : userInfo?.points ? (
                    formatRank(userInfo.points.rank)
                  ) : (
                    '--'
                  )}
                </div>
              </div>
            </div>

            {/* Next Update Time */}
            <div className="text-xs text-gray-400 mt-6">
              {SHOW_APR_BOOST ? (
                <>Next update: {getNextUpdateTime()}</>
              ) : (
                'APR Boost has now ended. This will be your final points.'
              )}
            </div>

            {/* Connect Wallet & Verify Section */}
            <div className="mt-6">
              {!isConnected ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-gray-400 text-xs">
                    <AlertCircle className="w-3.5 h-3.5" />
                    <span>Connect wallet to view points</span>
                  </div>
                  <button
                    onClick={() => open()}
                    className={`flex items-center gap-1.5 ${ACTION_BUTTON_CLASS}`}
                  >
                    <Wallet className="w-3.5 h-3.5" />
                    Connect Wallet
                  </button>
                </div>
              ) : isConnected && !isAuthenticated && SHOW_APR_BOOST ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-yellow-400 text-xs">
                    <AlertCircle className="w-3.5 h-3.5" />
                    <span>Please verify your account</span>
                  </div>
                  <button
                    onClick={onVerifyClick}
                    className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-yellow-500/60 via-orange-500/60 to-yellow-500/20 hover:from-yellow-500/40 hover:via-orange-500/40 hover:to-yellow-500/40 rounded-lg transition-all duration-300 border border-yellow-500/30 hover:border-yellow-500/50 cursor-pointer shadow-[0_0_15px_rgba(245,158,11,0.15)] hover:shadow-[0_0_25px_rgba(245,158,11,0.3)]"
                  >
                    <AlertCircle className="w-3.5 h-3.5" />
                    Verify Account
                  </button>
                </div>
              ) : null}
            </div>
          </div>

          {/* Right Side */}
          <div className="flex flex-col justify-center lg:pl-8 lg:border-l border-white/5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-8 md:gap-y-12 gap-x-8">
              {/* Total aprMON */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                  <BarChart3 className="w-4 h-4" />
                  <span>Total aprMON</span>
                </div>
                <div className="text-xl md:text-2xl font-semibold text-white">
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-white" />
                  ) : (
                    formatValue(totalAprmon)
                  )}
                </div>
                <button
                  onClick={() =>
                    window.open('https://app.capricorn.tech', '_blank')
                  }
                  className={ACTION_BUTTON_CLASS}
                >
                  Stake MON
                </button>
              </div>

              {/* Locked aprMON */}
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                  <BarChart3 className="w-4 h-4" />
                  <span>Locked aprMON</span>
                </div>
                <div className="text-xl md:text-2xl font-semibold text-white">
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin text-white" />
                  ) : (
                    formatValue(lockedAprmon)
                  )}
                </div>
                <button
                  onClick={() =>
                    window.open(
                      'https://participate.capricorn.tech/lock',
                      '_blank'
                    )
                  }
                  className={ACTION_BUTTON_CLASS}
                >
                  Lock MON
                </button>
              </div>

              {/* Swapr volume */}
              <div className="space-y-1 border-t border-white/5 pt-6 md:pt-8 col-span-1 sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-y-8 gap-x-8">
                <div>
                  <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                    <TrendingUp className="w-4 h-4" />
                    <span>Swapr volume</span>
                  </div>
                  <div className="text-xl md:text-2xl font-semibold text-white">
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin text-white" />
                    ) : userInfo?.totalVolume ? (
                      formatCurrency(userInfo.totalVolume)
                    ) : (
                      '$0.00'
                    )}
                  </div>
                  <button
                    onClick={() =>
                      window.open('https://app.capricorn.tech/swapr', '_blank')
                    }
                    className={ACTION_BUTTON_CLASS}
                  >
                    Swap
                  </button>
                </div>

                {/* $APR Staked */}
                <div>
                  <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
                    <BarChart3 className="w-4 h-4" />
                    <span>$APR Staked</span>
                  </div>
                  <div className="text-xl md:text-2xl font-semibold text-white">
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin text-white" />
                    ) : userInfo?.stakedApr ? (
                      formatValue(userInfo.stakedApr)
                    ) : (
                      '0'
                    )}
                  </div>
                  <button
                    onClick={() =>
                      window.open(
                        'https://participate.capricorn.tech/',
                        '_blank'
                      )
                    }
                    className={ACTION_BUTTON_CLASS}
                  >
                    Stake $APR
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
